/**
 * Client session location confirmations (design Decision 33): one `Encounter.location` entry per
 * client participant who joined (a Patient, or a RelatedPerson joining through their personal link). The entry
 * references a contained `Location` holding the address, `period.start` is when it was attested, and one
 * `client-session-location-confirmation` extension names the confirmer and carries the attestation fields. Guests who
 * cannot be matched to a participant are not asked, and nothing is stored for them.
 *
 * Confirmations on the Appointment (top-level extensions, with the address in sub-extensions) are
 * still read for a confirmer the Encounter has none for: older data, and appointments with no Encounter (intake and
 * other non-billable sessions), which are still written there ({@link applyLocationConfirmationToAppointment}).
 *
 * Shared by the UI (reading, provider edits) and the Chime bots, which write a joiner's own confirmation server-side
 * (clients and guests cannot update the Encounter).
 */
import type { Appointment, Encounter, EncounterLocation, Extension, Location } from '@medplum/fhirtypes';
import { CLIENT_LOCATION_CONFIRMATION_SUB_EXT, CLIENT_SESSION_LOCATION_CONFIRMATION_EXT } from '../constants';
import { setAppointmentExtensions } from './appointment';

/** Attestation copy shown to the joiner — version when this text changes. */
export const LOCATION_ATTESTATION_TEXT =
  'I confirm that I am physically located at this address for today’s session.';
export const LOCATION_ATTESTATION_VERSION = '1.0';

export const CLIENT_LOCATION_CONFIRMATION_SOURCES = [
  'device_location_prefilled_client_attested',
  'ip_prefilled_client_attested',
  'manual_client_attested',
  'saved_address_client_attested',
] as const;

export type ClientLocationConfirmationSource = (typeof CLIENT_LOCATION_CONFIRMATION_SOURCES)[number];

export type LocationPrefillSource = 'device_location' | 'saved_address' | 'manual' | 'none' | 'ip';

export interface LocationConfirmationInput {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  notAtUsualAddress: boolean;
  attestationAccepted: boolean;
  prefillSource: LocationPrefillSource;
  wasEditedAfterPrefill: boolean;
  ipDerivedCity?: string;
  ipDerivedState?: string;
}

/** Who confirmed: a client participant, `Patient/<id>` or `RelatedPerson/<id>`. */
export type LocationConfirmer = { ref: string };

export interface ClientLocationConfirmation {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  confirmedAt: string;
  attestationText: string;
  attestationVersion: string;
  source: ClientLocationConfirmationSource;
  /** Set when a Patient confirmed; the key most readers (notes, session panel) use. */
  confirmedByPatientId?: string;
  /** `Patient/<id>` or `RelatedPerson/<id>`. */
  confirmedByRef: string;
  practitionerId?: string;
  wasEditedAfterPrefill: boolean;
  ipDerivedCity?: string;
  ipDerivedState?: string;
  stateMismatch: boolean;
  licensureBlocking: boolean;
  notAtUsualAddress: boolean;
  appointmentId: string;
}

const SUB = CLIENT_LOCATION_CONFIRMATION_SUB_EXT;

function subExt(extensions: Extension[] | undefined, url: string): Extension | undefined {
  return extensions?.find((e) => e.url === url);
}

function subString(extensions: Extension[] | undefined, url: string): string | undefined {
  return subExt(extensions, url)?.valueString?.trim() || undefined;
}

function subBoolean(extensions: Extension[] | undefined, url: string): boolean {
  return subExt(extensions, url)?.valueBoolean === true;
}

function idOf(ref: string | undefined, type: string): string | undefined {
  const parts = ref?.split('/');
  return parts?.length === 2 && parts[0] === type && parts[1] ? parts[1] : undefined;
}

function confirmerOf(nested: Extension[] | undefined): string | undefined {
  return subExt(nested, SUB.confirmedByPatient)?.valueReference?.reference;
}

type ConfirmedAddress = Pick<ClientLocationConfirmation, 'street' | 'city' | 'state' | 'postalCode' | 'confirmedAt'>;

/** A confirmation from its attestation sub-extensions and its address; null when a required field is missing. */
function toConfirmation(
  nested: Extension[] | undefined,
  { street, city, state, postalCode, confirmedAt }: Partial<ConfirmedAddress>,
  appointmentId: string,
): ClientLocationConfirmation | null {
  const source = subString(nested, SUB.source) as ClientLocationConfirmationSource | undefined;
  const confirmedByRef = confirmerOf(nested);
  if (!street || !city || !state || !postalCode || !confirmedAt || !source || !confirmedByRef) {
    return null;
  }
  return {
    street,
    city,
    state,
    postalCode,
    confirmedAt,
    attestationText: subString(nested, SUB.attestationText) ?? LOCATION_ATTESTATION_TEXT,
    attestationVersion: subString(nested, SUB.attestationVersion) ?? LOCATION_ATTESTATION_VERSION,
    source,
    confirmedByPatientId: idOf(confirmedByRef, 'Patient'),
    confirmedByRef,
    practitionerId: idOf(subExt(nested, SUB.practitionerId)?.valueReference?.reference, 'Practitioner'),
    wasEditedAfterPrefill: subBoolean(nested, SUB.wasEditedAfterPrefill),
    ipDerivedCity: subString(nested, SUB.ipDerivedCity),
    ipDerivedState: subString(nested, SUB.ipDerivedState),
    stateMismatch: subBoolean(nested, SUB.stateMismatch),
    licensureBlocking: subBoolean(nested, SUB.licensureBlocking),
    notAtUsualAddress: subBoolean(nested, SUB.notAtUsualAddress),
    appointmentId,
  };
}

// ─── Appointment (no Encounter, and older data) ─────────────────────

/** Every confirmation extension stored on the Appointment (top level; client policies hide `extension`). */
export function getLocationConfirmationExtensions(appointment: Appointment): Extension[] {
  return (appointment.extension ?? []).filter((e) => e.url === CLIENT_SESSION_LOCATION_CONFIRMATION_EXT);
}

/** The confirmations stored on the Appointment (address and time in sub-extensions). */
export function getClientLocationConfirmationsFromAppointment(
  appointment: Appointment | null | undefined,
): ClientLocationConfirmation[] {
  if (!appointment?.id) return [];
  return getLocationConfirmationExtensions(appointment)
    .map(({ extension: nested }) =>
      toConfirmation(
        nested,
        {
          street: subString(nested, SUB.street),
          city: subString(nested, SUB.city),
          state: subString(nested, SUB.state),
          postalCode: subString(nested, SUB.postalCode),
          confirmedAt: subExt(nested, SUB.confirmedAt)?.valueDateTime,
        },
        appointment.id!,
      ),
    )
    .filter((c): c is ClientLocationConfirmation => c !== null);
}

// ─── Encounter ──────────────────────────────────────────────────────

const CONTAINED_LOCATION_ID_PREFIX = 'loc-';

function attestationOf(entry: EncounterLocation): Extension[] | undefined {
  return subExt(entry.extension, CLIENT_SESSION_LOCATION_CONFIRMATION_EXT)?.extension;
}

function containedIdOf(entry: EncounterLocation): string | undefined {
  const ref = entry.location?.reference;
  return ref?.startsWith('#') ? ref.slice(1) : undefined;
}

/** The confirmations stored on the Encounter. */
export function getClientLocationConfirmationsFromEncounter(
  encounter: Encounter | null | undefined,
): ClientLocationConfirmation[] {
  if (!encounter) return [];
  const appointmentId = idOf(encounter.appointment?.[0]?.reference, 'Appointment') ?? '';
  return (encounter.location ?? [])
    .map((entry) => {
      const nested = attestationOf(entry);
      if (!nested) return null;
      const id = containedIdOf(entry);
      const address = encounter.contained?.find((r): r is Location => r.resourceType === 'Location' && r.id === id)
        ?.address;
      return toConfirmation(
        nested,
        {
          street: address?.line?.[0]?.trim(),
          city: address?.city?.trim(),
          state: address?.state?.trim(),
          postalCode: address?.postalCode?.trim(),
          confirmedAt: entry.period?.start,
        },
        appointmentId,
      );
    })
    .filter((c): c is ClientLocationConfirmation => c !== null);
}

/**
 * Every client's confirmation: the Encounter's, then the Appointment's (older data, Zoom production included) for
 * confirmers the Encounter has none for.
 */
export function getClientLocationConfirmations(
  encounter: Encounter | null | undefined,
  appointment: Appointment | null | undefined,
): ClientLocationConfirmation[] {
  const current = getClientLocationConfirmationsFromEncounter(encounter);
  const confirmers = new Set(current.map((c) => c.confirmedByRef));
  return [
    ...current,
    ...getClientLocationConfirmationsFromAppointment(appointment).filter((c) => !confirmers.has(c.confirmedByRef)),
  ];
}

/** The confirmer's confirmation, if any (the Encounter's, else the Appointment's). */
/**
 * The appointment's session Encounter among the Encounters that reference it: the one whose subject is a Patient
 * participant of the appointment, and only when exactly one is (`appointment-encounter-creation` makes one per
 * appointment for its first Patient). Staff can create Encounters that point at any Appointment, so any other
 * Encounter is ignored and the confirmation stays on the Appointment. The Chime bots and the UI both use this.
 */
export function pickSessionEncounter(encounters: Encounter[], appointment: Appointment): Encounter | undefined {
  const patients = new Set(
    (appointment.participant ?? [])
      .map((p) => p.actor?.reference)
      .filter((ref): ref is string => !!ref?.startsWith('Patient/')),
  );
  const candidates = encounters.filter((encounter) => patients.has(encounter.subject?.reference ?? ''));
  return candidates.length === 1 ? candidates[0] : undefined;
}

export function findLocationConfirmation(
  encounter: Encounter | null | undefined,
  appointment: Appointment | null | undefined,
  confirmer: LocationConfirmer,
): ClientLocationConfirmation | null {
  return (
    getClientLocationConfirmations(encounter, appointment).find((c) => c.confirmedByRef === confirmer.ref) ?? null
  );
}

function resolveConfirmationSource(input: LocationConfirmationInput): ClientLocationConfirmationSource {
  if (input.prefillSource === 'device_location') return 'device_location_prefilled_client_attested';
  if (input.prefillSource === 'ip') return 'ip_prefilled_client_attested';
  if (input.prefillSource === 'saved_address') return 'saved_address_client_attested';
  return 'manual_client_attested';
}

/** The entry's attestation sub-extensions; the address is on the contained Location and the time is `period.start`. */
function buildAttestationExtensions(confirmation: ClientLocationConfirmation): Extension[] {
  const exts: Extension[] = [
    { url: SUB.confirmedByPatient, valueReference: { reference: confirmation.confirmedByRef } },
    { url: SUB.source, valueString: confirmation.source },
    { url: SUB.attestationVersion, valueString: confirmation.attestationVersion },
    { url: SUB.attestationText, valueString: confirmation.attestationText },
    { url: SUB.wasEditedAfterPrefill, valueBoolean: confirmation.wasEditedAfterPrefill },
    { url: SUB.stateMismatch, valueBoolean: confirmation.stateMismatch },
    { url: SUB.licensureBlocking, valueBoolean: confirmation.licensureBlocking },
    { url: SUB.notAtUsualAddress, valueBoolean: confirmation.notAtUsualAddress },
  ];
  if (confirmation.ipDerivedCity) exts.push({ url: SUB.ipDerivedCity, valueString: confirmation.ipDerivedCity });
  if (confirmation.ipDerivedState) exts.push({ url: SUB.ipDerivedState, valueString: confirmation.ipDerivedState });
  if (confirmation.practitionerId) {
    exts.push({ url: SUB.practitionerId, valueReference: { reference: `Practitioner/${confirmation.practitionerId}` } });
  }
  return exts;
}

export function buildLocationConfirmation(
  appointment: Appointment,
  confirmer: LocationConfirmer,
  input: LocationConfirmationInput,
  opts?: { sourceOverride?: ClientLocationConfirmationSource; licensureBlocking?: boolean; now?: Date },
): ClientLocationConfirmation {
  const practitionerId = idOf(
    appointment.participant?.find((p) => p.actor?.reference?.startsWith('Practitioner/'))?.actor?.reference,
    'Practitioner',
  );
  const state = input.state.trim().toUpperCase();
  const ipDerivedState = input.ipDerivedState?.trim()?.toUpperCase() || undefined;
  return {
    street: input.street.trim(),
    city: input.city.trim(),
    state,
    postalCode: input.postalCode.trim(),
    confirmedAt: (opts?.now ?? new Date()).toISOString(),
    attestationText: LOCATION_ATTESTATION_TEXT,
    attestationVersion: LOCATION_ATTESTATION_VERSION,
    source: opts?.sourceOverride ?? resolveConfirmationSource(input),
    confirmedByRef: confirmer.ref,
    confirmedByPatientId: idOf(confirmer.ref, 'Patient'),
    practitionerId,
    wasEditedAfterPrefill: input.wasEditedAfterPrefill,
    ipDerivedCity: input.ipDerivedCity?.trim() || undefined,
    ipDerivedState,
    stateMismatch: Boolean(ipDerivedState && ipDerivedState !== state),
    licensureBlocking: opts?.licensureBlocking ?? false,
    notAtUsualAddress: input.notAtUsualAddress,
    appointmentId: appointment.id ?? '',
  };
}

/**
 * Upserts one confirmer's entry with its contained Location (a provider edit replaces it); every other entry and
 * contained resource is kept. Returns a new Encounter.
 */
export function applyLocationConfirmationToEncounter(
  encounter: Encounter,
  confirmation: ClientLocationConfirmation,
): Encounter {
  const isMine = (entry: EncounterLocation): boolean =>
    confirmerOf(attestationOf(entry)) === confirmation.confirmedByRef;
  const replacedIds = new Set((encounter.location ?? []).filter(isMine).map(containedIdOf));
  const contained = (encounter.contained ?? []).filter((r) => !(r.resourceType === 'Location' && replacedIds.has(r.id)));
  const taken = contained.map((r) =>
    r.id?.startsWith(CONTAINED_LOCATION_ID_PREFIX) ? Number(r.id.slice(CONTAINED_LOCATION_ID_PREFIX.length)) || 0 : 0,
  );
  const id = `${CONTAINED_LOCATION_ID_PREFIX}${Math.max(0, ...taken) + 1}`;
  const address: Location = {
    resourceType: 'Location',
    id,
    address: {
      line: [confirmation.street],
      city: confirmation.city,
      state: confirmation.state,
      postalCode: confirmation.postalCode,
      country: 'US',
    },
  };
  return {
    ...encounter,
    contained: [...contained, address],
    location: [
      ...(encounter.location ?? []).filter((entry) => !isMine(entry)),
      {
        location: { reference: `#${id}` },
        status: 'active',
        period: { start: confirmation.confirmedAt },
        extension: [{ url: CLIENT_SESSION_LOCATION_CONFIRMATION_EXT, extension: buildAttestationExtensions(confirmation) }],
      },
    ],
  };
}

/**
 * Like {@link applyLocationConfirmationToEncounter}, but never replaces an attestation: returns `encounter` itself
 * when the confirmer already has one, on the Encounter or (older data) on `appointment`.
 */
export function applyLocationConfirmationIfAbsent(
  encounter: Encounter,
  appointment: Appointment | null | undefined,
  confirmation: ClientLocationConfirmation,
): Encounter {
  return findLocationConfirmation(encounter, appointment, { ref: confirmation.confirmedByRef })
    ? encounter
    : applyLocationConfirmationToEncounter(encounter, confirmation);
}

/**
 * Upserts one confirmer's confirmation on the Appointment, only for an appointment with no Encounter (intake and
 * other non-billable sessions). Every other entry is kept.
 */
export function applyLocationConfirmationToAppointment(
  appointment: Appointment,
  confirmation: ClientLocationConfirmation,
): Appointment {
  const others = getLocationConfirmationExtensions(appointment).filter(
    (e) => confirmerOf(e.extension) !== confirmation.confirmedByRef,
  );
  const next = { ...appointment };
  setAppointmentExtensions(next, CLIENT_SESSION_LOCATION_CONFIRMATION_EXT, [
    ...others,
    {
      url: CLIENT_SESSION_LOCATION_CONFIRMATION_EXT,
      extension: [
        { url: SUB.street, valueString: confirmation.street },
        { url: SUB.city, valueString: confirmation.city },
        { url: SUB.state, valueString: confirmation.state },
        { url: SUB.postalCode, valueString: confirmation.postalCode },
        { url: SUB.confirmedAt, valueDateTime: confirmation.confirmedAt },
        ...buildAttestationExtensions(confirmation),
      ],
    },
  ]);
  return next;
}

/** Every refusal {@link parseLocationConfirmationInput} returns, so a caller can tell them from other errors. */
export const LOCATION_INPUT_ERRORS = {
  missing: 'location is required',
  required: 'street, city, state and postal code are required',
  tooLong: 'an address field is too long',
  state: 'state must be a 2-letter code',
  attestation: 'the location attestation must be accepted',
} as const;

/** True when `message` is one of the location validator's refusals. */
export function isLocationInputError(message: string): boolean {
  return Object.values(LOCATION_INPUT_ERRORS).some((m) => message.includes(m));
}

const MAX_LENGTH = { street: 200, city: 100, state: 2, postalCode: 10 } as const;

/**
 * Validates a joiner-submitted confirmation body (bots receive it as untrusted JSON). Returns the input with
 * strings trimmed, or one of {@link LOCATION_INPUT_ERRORS}.
 */
export function parseLocationConfirmationInput(raw: unknown): LocationConfirmationInput | string {
  if (!raw || typeof raw !== 'object') return LOCATION_INPUT_ERRORS.missing;
  const r = raw as Record<string, unknown>;
  const trimmed = (k: string): string => (typeof r[k] === 'string' ? (r[k] as string).trim() : '');
  const street = trimmed('street');
  const city = trimmed('city');
  const state = trimmed('state');
  const postalCode = trimmed('postalCode');
  if (!street || !city || !state || !postalCode) return LOCATION_INPUT_ERRORS.required;
  if (
    street.length > MAX_LENGTH.street ||
    city.length > MAX_LENGTH.city ||
    postalCode.length > MAX_LENGTH.postalCode
  ) {
    return LOCATION_INPUT_ERRORS.tooLong;
  }
  if (!/^[A-Za-z]{2}$/.test(state)) return LOCATION_INPUT_ERRORS.state;
  if (r.attestationAccepted !== true) return LOCATION_INPUT_ERRORS.attestation;
  const str = (k: string, max: number): string | undefined => {
    const v = trimmed(k);
    return v && v.length <= max ? v : undefined;
  };
  const prefill = r.prefillSource;
  const prefillSource: LocationPrefillSource =
    prefill === 'device_location' || prefill === 'saved_address' || prefill === 'manual' || prefill === 'ip'
      ? prefill
      : 'none';
  return {
    street,
    city,
    state,
    postalCode,
    notAtUsualAddress: r.notAtUsualAddress === true,
    attestationAccepted: true,
    prefillSource,
    wasEditedAfterPrefill: r.wasEditedAfterPrefill === true,
    ipDerivedCity: str('ipDerivedCity', MAX_LENGTH.city),
    ipDerivedState: str('ipDerivedState', MAX_LENGTH.state),
  };
}
